import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, createContext, useContext } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { Button, ConfirmationDialog } from '../ui';
import FormLayoutRenderer from './FormLayoutRenderer';
import { renderField, FieldConfig } from './fieldRenderers';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { validateField as validateFieldUtil } from '../../utils/validation';
import ValidationMessage from '../Form/ValidationMessage';
import DynamicFieldRenderer from '../Form/DynamicFieldRenderer';
import { getEntityTypeFromRoute, getEntityTypeFromTitle } from '../../utils/entityTypeMapper';
import { setCustomFieldValue } from '../../modules/custom-fields/useData';
import { useUserSettings } from '../../modules/settings/useData';
import { FileAttachmentsFormBlock } from '../attachments/FileAttachmentsFormBlock';
import type { FormLayoutVariant } from './FormLayoutRenderer';
import { resolveCommonAction } from '../../i18n/labels';
import { useModuleTranslation } from '../../i18n/useModuleTranslation';
import {
  translateFieldLabel,
  translateFieldPlaceholder,
  translateSectionTitle,
} from '../../i18n/moduleT';
import { readAiFormPrefill, clearAiFormPrefill, AI_FORM_PREFILL_STATE_KEY, type AiFormPrefillLocationState } from '../../modules/ai/aiFormPrefill';
import { useAuth } from '../../contexts/AuthContext';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const msg = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (msg) return msg;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

// Form data lives in a ref so typing does not cause parent re-renders. Fields use local state
// initialized once from this ref at mount, and write back to the ref on change.
type FormDataContextValue = {
  formDataRef: React.MutableRefObject<Record<string, any>>;
  setFieldValue: (name: string, value: any) => void;
  /** Set multiple fields at once and bump formDataVersion once (e.g. prefill category + subcategory from payee without clearing subcategory) */
  setMultipleFieldValues: (updates: Record<string, any>) => void;
  clearFieldError: (name: string) => void;
  formLayoutVariant?: FormLayoutVariant;
  /** When this changes (e.g. edit data loaded), fields sync their local state from formDataRef */
  formKey: string;
  /** Bumps when a field that other fields depend on (e.g. category) changes, so dynamic-option fields refetch */
  formDataVersion: number;
};
const FormDataContext = createContext<FormDataContextValue | null>(null);

export { FormDataContext };
export type { FormDataContextValue };

// Field that owns its value in local state (initialized from ref at mount). Only this field
// re-renders on keystroke; parent and sibling fields do not. Memoized so section edit toggles
// only re-render fields in that section (via disabledOverride prop).
interface FormFieldWithLocalStateProps {
  fieldConfig: FieldConfig;
  error: string | undefined;
  onBlur: (name: string, value: any) => void;
  config: any;
  /** When true, field is disabled (e.g. section not in edit mode). Passed from parent to avoid context-driven re-renders. */
  disabledOverride?: boolean;
  isEdit: boolean;
}
const FormFieldWithLocalState = React.memo(function FormFieldWithLocalState({ fieldConfig, error, onBlur, config, disabledOverride, isEdit }: FormFieldWithLocalStateProps) {
  const ctx = useContext(FormDataContext);
  if (!ctx) throw new Error('FormFieldWithLocalState must be used inside FormDataContext');
  const { formDataRef, setFieldValue, setMultipleFieldValues, clearFieldError, formLayoutVariant, formKey, formDataVersion } = ctx;
  const [value, setValue] = useState(() => formDataRef.current[fieldConfig.name]);
  // Sync from formDataRef when edit data loads (formKey) or when a dependency changes (formDataVersion, e.g. client → invoice cleared).
  // useLayoutEffect so dependent fields (e.g. invoice list) see cleared values in the same frame as the new client id.
  useLayoutEffect(() => {
    setValue(formDataRef.current[fieldConfig.name]);
  }, [formKey, formDataVersion, fieldConfig.name, formDataRef]);
  const filterField = config?.filters?.fields?.find((f: any) => f.key === fieldConfig.name);
  // Compute during render so we always pass latest fieldConfig (including options) — avoid useMemo stale options
  const mergedFieldConfig: FieldConfig = {
    ...fieldConfig,
    apiEndpoint: fieldConfig.apiEndpoint || filterField?.apiEndpoint,
    labelField: fieldConfig.labelField || filterField?.labelField,
    disabled: fieldConfig.disabled || disabledOverride,
  };
  const getFormData = useCallback(() => formDataRef.current, [formDataRef]);
  const handleChange = useCallback((name: string, newValue: any) => {
    setValue(newValue);
    setFieldValue(name, newValue);
    clearFieldError(name);
    const sideEffect = fieldConfig.onChangeSideEffect;
    const runOnClose = fieldConfig.runSideEffectOnClose;
    if (sideEffect && !runOnClose) {
      Promise.resolve(sideEffect(newValue, setFieldValue, setMultipleFieldValues, getFormData)).catch(() => {});
    }
  }, [setFieldValue, setMultipleFieldValues, clearFieldError, fieldConfig.onChangeSideEffect, fieldConfig.runSideEffectOnClose, getFormData]);
  const onSelectClose = useCallback(
    (closeValue: any) => {
      const sideEffect = fieldConfig.onChangeSideEffect;
      if (fieldConfig.runSideEffectOnClose && sideEffect) {
        Promise.resolve(sideEffect(closeValue, setFieldValue, setMultipleFieldValues, getFormData)).catch(() => {});
      }
    },
    [fieldConfig.runSideEffectOnClose, fieldConfig.onChangeSideEffect, setFieldValue, setMultipleFieldValues, getFormData]
  );
  return renderField({
    field: mergedFieldConfig,
    value,
    onChange: handleChange,
    setFieldValue,
    setMultipleFieldValues,
    onBlur: (name: string, val: any) => onBlur(name, val),
    onSelectClose: fieldConfig.runSideEffectOnClose ? onSelectClose : undefined,
    error,
    getFormData,
    formLayoutVariant,
    formKey,
    formDataVersion,
    isEdit,
  });
});

interface GenericFormPageProps<T> {
  // Data hook that returns { data/client, isLoading, mutate }
  useData: (id: number) => {
    data?: T | undefined;
    client?: T | undefined;
    isLoading: boolean;
    mutate: () => void | any;
  };
  // Module configuration
  config: {
    module: {
      i18nKey?: string;
      permissionModuleKey?: string;
      route: {
        list: string;
        new: string;
        detail: (id: number) => string;
        edit: (id: number) => string;
      };
    };
    form: {
      new: {
        title: string;
        subtitle?: string;
        submitText: string;
      };
      edit: {
        title: string;
        subtitle?: string;
        submitText: string;
      };
      cancelText: string;
      loadingText: string;
      errorMessage: string;
      supportsFiles?: boolean;
      /** When set, edit form can open existing attachments (GET blob) in a new tab. `entityId` is the record id from the route. */
      getViewFileUrl?: (entityId: number, fileId: number) => string;
      create: (data: Partial<T>, files?: File[], filesToDetach?: number[]) => Promise<T>;
      update: (id: number, data: Partial<T>, files?: File[], filesToDetach?: number[]) => Promise<T>;
      /** When true for current item (edit mode), all form fields are read-only */
      disableWhen?: (item: T) => boolean;
      /** Shown when form is read-only due to disableWhen (e.g. "Offer already accepted") */
      disabledMessage?: string | ((item: T) => string);
      /** Tailwind max-width class for the form container on desktop (default: max-w-6xl). */
      contentMaxWidthClass?: string;
    };
    fields: FieldConfig[];
    formLayout: {
      mode?: 'default' | 'tabs';
      sections: Array<{
        id: string;
        title: string;
        icon?: string | React.ReactNode;
        fields: string[];
        columns?: number;
        collapsible?: boolean;
        fieldSpans?: Record<string, string>;
        /** When set, section is shown only if predicate is true (e.g. posting-account-only fields). */
        visibleWhen?: (formData: Record<string, unknown>) => boolean;
      }>;
    };
    getInitialFormData: () => Partial<T>;
    mapItemToFormData: (item: T) => Partial<T>;
    /** When provided, rendered below the main layout. Use for e.g. product photos: on new show a hint, on edit show upload+gallery. */
    renderEditFormExtra?: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode;
    /** When provided, rendered above the main layout (e.g. profile photo on user edit). */
    renderEditFormHeader?: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode;
    delete?: {
      title: string;
      message: string;
      confirmText: string;
      cancelText: string;
      errorMessage: string;
      handler: (id: number) => Promise<void>;
    };
  };
  /** When provided, these options override config field options so selects always get live data (e.g. clientOptions). */
  fieldOptions?: Record<string, Array<{ value: string | number; label: string }>>;
}

function extractCreatedEntityId(result: unknown, fallbackEditId?: number): number | null {
  if (fallbackEditId != null && Number.isInteger(fallbackEditId)) {
    return fallbackEditId;
  }
  const root = result && typeof result === 'object' ? (result as Record<string, unknown>) : null;
  const payload =
    root?.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  if (!payload) return null;
  const candidates = [
    payload.id,
    payload.todoId,
    payload.clientAppId,
    payload.clientId,
    payload.taskId,
    payload.leadId,
    payload.expenseId,
    payload.ticketId,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
  }
  return null;
}

function GenericFormPage<T extends { [key: string]: any }>({
  useData,
  config,
  fieldOptions,
}: GenericFormPageProps<T>) {
  const { id: idParam } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();
  // Only treat as edit when id is a valid numeric string (avoids "new" or invalid id → NaN)
  const editId = idParam && idParam !== 'new' ? parseInt(idParam, 10) : NaN;
  const isEdit = Number.isInteger(editId);
  const id = isEdit ? String(editId) : idParam;
  const isMobile = false;
  const dataHookResult = useData(isEdit ? editId : 0);
  const data = (dataHookResult.data || dataHookResult.client) as T | undefined;
  const loadingData = dataHookResult.isLoading;
  const { t } = useTranslation('common');
  const i18nKey = config.module.i18nKey;
  const { mt } = useModuleTranslation(i18nKey);
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { can } = useAuth();
  const { userSettings } = useUserSettings();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const formLayoutVariant: FormLayoutVariant =
    userSettings?.editFormLayout === 'minimal' ? 'minimal' : userSettings?.editFormLayout === 'compact' ? 'compact' : 'default';

  const permissionModuleKey = (config.module as { permissionModuleKey?: string }).permissionModuleKey;
  const deleteConfig = config.delete;
  const canDelete =
    isEdit &&
    Number.isInteger(editId) &&
    Boolean(deleteConfig) &&
    (!permissionModuleKey || can(permissionModuleKey, 'delete'));

  const handleDeleteRecord = useCallback(async () => {
    if (!deleteConfig || !Number.isInteger(editId)) return;
    setDeleteLoading(true);
    try {
      await deleteConfig.handler(editId);
      showSuccess(t('states.deletedSuccessfully', { defaultValue: 'Item deleted successfully' }));
      setDeleteOpen(false);
      navigate(config.module.route.list);
    } catch (err: unknown) {
      showError(getErrorMessage(err, deleteConfig.errorMessage));
    } finally {
      setDeleteLoading(false);
    }
  }, [config.module.route.list, deleteConfig, editId, navigate, showError, showSuccess, t]);

  const formDataRef = useRef<Record<string, any>>({} as Record<string, any>);
  const formKeyRef = useRef<string>('');
  const aiPrefillCacheRef = useRef<Record<string, unknown> | null | undefined>(undefined);

  const resolveAiPrefill = useCallback((): Record<string, unknown> | null => {
    if (aiPrefillCacheRef.current !== undefined) {
      return aiPrefillCacheRef.current;
    }

    const stateFields = (location.state as AiFormPrefillLocationState | null)?.[AI_FORM_PREFILL_STATE_KEY];
    if (stateFields && typeof stateFields === 'object') {
      const normalized: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(stateFields)) {
        if (value !== undefined && value !== null && value !== '') {
          normalized[key] = value;
        }
      }
      if (Object.keys(normalized).length > 0) {
        aiPrefillCacheRef.current = normalized;
        return normalized;
      }
    }

    const stored = !isEdit ? readAiFormPrefill(location.pathname) : null;
    aiPrefillCacheRef.current = stored;
    return stored;
  }, [isEdit, location.pathname, location.state]);

  const buildInitialFormData = useCallback((): Partial<T> => {
    const initialData = config.getInitialFormData();
    // Pre-fill form data from URL search params (e.g., ?clientId=123)
    if (!isEdit) {
      const aiPrefill = resolveAiPrefill();
      if (aiPrefill) {
        for (const [key, value] of Object.entries(aiPrefill)) {
          const coerced =
            typeof value === 'string' && /^\d+$/.test(value) ? parseInt(value, 10) : value;
          (initialData as Record<string, unknown>)[key] = coerced;
        }
      }
      searchParams.forEach((value, key) => {
        if (key === 'fromAi') return;
        // Try to parse as number if it's a numeric string
        const numValue = /^\d+$/.test(value) ? parseInt(value, 10) : value;
        (initialData as any)[key] = numValue;
      });
    }
    return initialData;
  }, [config, isEdit, searchParams, resolveAiPrefill]);

  const formKey = isEdit ? `edit-${id}-${data ? 'loaded' : 'loading'}` : `new-${location.key}`;
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const formConfigTyped = config.form as {
    supportsFiles?: boolean;
    getViewFileUrl?: (entityId: number, fileId: number) => string;
  };
  const supportsFiles = !!formConfigTyped.supportsFiles;
  const getViewFileUrlForEdit =
    isEdit && Number.isInteger(editId) && formConfigTyped.getViewFileUrl
      ? (fileId: number) => formConfigTyped.getViewFileUrl!(editId, fileId)
      : undefined;
  const [files, setFiles] = useState<File[]>([]);
  const [filesToDetach, setFilesToDetach] = useState<number[]>([]);
  const existingFiles = (data as any)?.FileAttachment ?? [];
  const visibleExistingFiles = useMemo(
    () => existingFiles.filter((f: { id: number }) => !filesToDetach.includes(f.id)),
    [existingFiles, filesToDetach]
  );

  const prevEditIdRef = useRef<number | null>(null);
  if (formKey !== formKeyRef.current) {
    formKeyRef.current = formKey;
    aiPrefillCacheRef.current = undefined;
    formDataRef.current = (isEdit && data ? config.mapItemToFormData(data) : buildInitialFormData()) as Record<string, any>;
    if (!isEdit && resolveAiPrefill()) {
      clearAiFormPrefill();
    }
  } else if (Object.keys(formDataRef.current).length === 0) {
    formDataRef.current = buildInitialFormData() as Record<string, any>;
    if (!isEdit && resolveAiPrefill()) {
      clearAiFormPrefill();
    }
  }

  useEffect(() => {
    if (!isEdit || !Number.isInteger(editId)) {
      prevEditIdRef.current = null;
      return;
    }
    if (prevEditIdRef.current === editId) return;
    prevEditIdRef.current = editId;
    formKeyRef.current = `edit-${editId}-loading`;
    formDataRef.current = buildInitialFormData() as Record<string, any>;
    setFormErrors({});
    setFormDataVersion((v) => v + 1);
  }, [editId, isEdit, buildInitialFormData]);

  // Keep URL / AI prefill in sync when opening a new form (fields mount once from formDataRef).
  useLayoutEffect(() => {
    if (isEdit) return;
    const updates: Record<string, unknown> = {};
    const aiPrefill = resolveAiPrefill();
    if (aiPrefill) {
      for (const [key, value] of Object.entries(aiPrefill)) {
        const coerced =
          typeof value === 'string' && /^\d+$/.test(value) ? parseInt(value, 10) : value;
        if (formDataRef.current[key] !== coerced) {
          updates[key] = coerced;
        }
      }
    }
    searchParams.forEach((value, key) => {
      if (key === 'fromAi') return;
      const numValue = /^\d+$/.test(value) ? parseInt(value, 10) : value;
      if (formDataRef.current[key] !== numValue) {
        updates[key] = numValue;
      }
    });
    if (Object.keys(updates).length > 0) {
      formDataRef.current = { ...formDataRef.current, ...updates };
      setFormDataVersion((v) => v + 1);
    }
  }, [isEdit, searchParams, resolveAiPrefill]);

  const { dependencyFieldNames, dependentFieldsByDep } = useMemo(() => {
    const depSet = new Set<string>();
    const depToFields: Record<string, string[]> = {};
    config.fields.forEach((f: { name: string; optionsDependOnFields?: string[] }) => {
      (f.optionsDependOnFields || []).forEach((dep: string) => {
        depSet.add(dep);
        if (!depToFields[dep]) depToFields[dep] = [];
        depToFields[dep].push(f.name);
      });
    });
    return { dependencyFieldNames: depSet, dependentFieldsByDep: depToFields };
  }, [config.fields]);

  const [formDataVersion, setFormDataVersion] = useState(0);

  const fieldToSectionMap = useMemo(() => {
    const map: Record<string, string> = {};
    config.formLayout?.sections?.forEach((section) => {
      section.fields.forEach((fieldName) => {
        map[fieldName] = section.id;
      });
    });
    return map;
  }, [config.formLayout]);

  const initialSectionEditState = useMemo(() => {
    const state: Record<string, boolean> = {};
    config.formLayout?.sections?.forEach((section) => {
      if ((section as { toggleable?: boolean; defaultDisabled?: boolean }).toggleable) {
        state[section.id] = (section as { defaultDisabled?: boolean }).defaultDisabled !== true;
      }
    });
    return state;
  }, [config.formLayout]);
  const [sectionEditState, setSectionEditState] = useState<Record<string, boolean>>(initialSectionEditState);
  const onSectionEditToggle = useCallback((sectionId: string, enabled: boolean) => {
    setSectionEditState((prev) => ({ ...prev, [sectionId]: enabled }));
  }, []);

  const setFieldValue = useCallback((name: string, value: any) => {
    formDataRef.current = { ...formDataRef.current, [name]: value };
    if (dependencyFieldNames.has(name)) {
      const dependentNames = dependentFieldsByDep[name] || [];
      dependentNames.forEach((fieldName: string) => {
        const field = config.fields.find((f: { name: string; type?: string }) => f.name === fieldName);
        const emptyValue = field?.type === 'multiselect' ? [] : '';
        formDataRef.current = { ...formDataRef.current, [fieldName]: emptyValue };
      });
      setFormDataVersion((v) => v + 1);
    }
    // Re-render layout so section visibleWhen (e.g. IMAP vs POP3, COA isPosting, vessel fields) sees fresh formDataRef — refs alone do not re-render parents.
    if (name === 'incomingProtocol' || name === 'isPosting' || name === 'isVessel') {
      setFormDataVersion((v) => v + 1);
    }
  }, [dependencyFieldNames, dependentFieldsByDep, config.fields]);

  const setMultipleFieldValues = useCallback((updates: Record<string, any>) => {
    formDataRef.current = { ...formDataRef.current, ...updates };
    // Defer version bump so the current interaction (e.g. select closing) completes and UI stays responsive
    setTimeout(() => setFormDataVersion((v) => v + 1), 0);
  }, []);

  const handleFileChange = useCallback((newFiles: File[]) => {
    setFiles(newFiles);
  }, []);

  const handleRemoveExistingFile = useCallback((fileId: number) => {
    setFilesToDetach((prev) => [...prev, fileId]);
  }, []);

  // Helper: go back to where the user came from, falling back to the module list
  const goBack = useCallback(() => {
    // If there is history, go back; otherwise fall back to the list route
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(config.module.route.list);
    }
  }, [navigate, config.module.route.list]);

  useEffect(() => {
    if (isEdit && Number.isInteger(editId)) {
      const moduleApi = (config.module as { api?: { detail?: (id: number) => string } }).api;
      const detailKey = moduleApi?.detail?.(editId);
      if (detailKey) globalMutate(detailKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId]);

  const clearFieldError = useCallback((name: string) => {
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const formDataContextValue = useMemo<FormDataContextValue>(() => ({
    formDataRef,
    setFieldValue,
    setMultipleFieldValues,
    clearFieldError,
    formLayoutVariant,
    formKey,
    formDataVersion,
  }), [setFieldValue, setMultipleFieldValues, clearFieldError, formLayoutVariant, formKey, formDataVersion]);

  const handleBlur = useCallback((name: string, value: any) => {
    const field = config.fields.find((f) => f.name === name);
    if (field && field.validation) {
      const error = validateField(field, value, formDataRef.current as Partial<T>);
      if (error) {
        setFormErrors((prev) => ({
          ...prev,
          [name]: error,
        }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  }, [config.fields]);

  const validateField = (field: FieldConfig, value: any, formDataForValidation?: Partial<T>): string | undefined => {
    if (!field.validation) return undefined;

    const { validation } = field;
    const isRequired =
      validation.required === true ||
      (typeof validation.requiredWhen === 'function' && formDataForValidation && validation.requiredWhen(formDataForValidation));

    // Map field type to validation type
    let validationType: 'email' | 'phone' | 'number' | 'date' | 'url' | undefined;
    if (field.type === 'email') validationType = 'email';
    else if (field.type === 'tel' || field.type === 'phone') validationType = 'phone';
    else if (field.type === 'number') validationType = 'number';
    else if (field.type === 'date' || field.type === 'datetime') validationType = 'date';
    else if (field.type === 'url') validationType = 'url';

    // Use the validation utility
    const result = validateFieldUtil(value, {
      required: isRequired,
      type: validationType,
      min: validation.min !== undefined ? validation.min : validation.minLength,
      max: validation.max !== undefined ? validation.max : validation.maxLength,
      pattern: validation.pattern,
      patternMessage: validation.message,
    });

    if (!result.valid) {
      return validation.message || result.message || `${field.label} is invalid`;
    }

    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const currentData = formDataRef.current as Partial<T>;
    const errors: Record<string, string> = {};
    config.fields.forEach((field) => {
      const fieldError = validateField(field, currentData[field.name as keyof T], currentData);
      if (fieldError) {
        errors[field.name] = fieldError;
      }
    });

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      let createdEntityId: number | null = null;
      
      if (isEdit && Number.isInteger(editId)) {
        await config.form.update(
          editId,
          currentData,
          supportsFiles && files.length > 0 ? files : undefined,
          supportsFiles && filesToDetach.length > 0 ? filesToDetach : undefined
        );
        showSuccess(`${config.module.route.list.includes('clients') ? 'Client' : 'Item'} updated successfully`);
        createdEntityId = editId;
      } else {
        const result = await config.form.create(
          currentData,
          supportsFiles && files.length > 0 ? files : undefined,
          supportsFiles && filesToDetach.length > 0 ? filesToDetach : undefined
        );
        showSuccess(`${config.module.route.list.includes('clients') ? 'Client' : 'Item'} created successfully`);
        createdEntityId = extractCreatedEntityId(result);
      }

      if (createdEntityId) {
        const entityType = getEntityTypeFromRoute(config.module.route.list) ||
          getEntityTypeFromTitle((config.module as { title?: string }).title || '');
        if (entityType) {
          const customFieldEntries = Object.entries(currentData).filter(([key]) =>
            key.startsWith('_customFields_'),
          );
          for (const [key, value] of customFieldEntries) {
            const fieldId = parseInt(key.replace('_customFields_', ''), 10);
            if (!Number.isNaN(fieldId) && value !== undefined && value !== null) {
              try {
                await setCustomFieldValue(entityType, createdEntityId, fieldId, value);
              } catch (err: unknown) {
                console.error(`Failed to save custom field ${fieldId}:`, err);
              }
            }
          }
        }
      }

      // Revalidate list (+ saved record detail when known) without invalidating unrelated cache keys
      const listApiBase = (config.module as { api?: { list: string } }).api?.list;
      const moduleApi = (config.module as { api?: { detail?: (id: number) => string } }).api;
      if (listApiBase) {
        void globalMutate(listApiBase);
      }
      if (createdEntityId && moduleApi?.detail) {
        void globalMutate(moduleApi.detail(createdEntityId));
      }
      if (dataHookResult.mutate) {
        void dataHookResult.mutate();
      }

      const redirectToEdit = (config.form as { redirectToEditAfterCreate?: boolean }).redirectToEditAfterCreate;
      const fromAi = searchParams.get('fromAi') === '1';
      if (!isEdit && createdEntityId && fromAi) {
        navigate(config.module.route.detail(createdEntityId), { replace: true });
      } else if (!isEdit && createdEntityId && redirectToEdit) {
        navigate(config.module.route.edit(createdEntityId), { replace: true });
      } else if (isEdit && createdEntityId) {
        navigate(config.module.route.detail(createdEntityId), { replace: true });
      } else {
        navigate(config.module.route.list, { replace: true });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || config.form.errorMessage;
      setError(errorMessage);
      showError(errorMessage);
      console.error('Error saving:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    goBack();
  };

  const isFormReadOnlyByConfig = !!(config.form.disableWhen && data && config.form.disableWhen(data));

  const localizedFormLayout = useMemo(() => {
    if (!i18nKey) return config.formLayout;
    return {
      ...config.formLayout,
      sections: config.formLayout.sections.map((section) => ({
        ...section,
        title: translateSectionTitle(mt, section.id, section.title),
      })),
    };
  }, [config.formLayout, i18nKey, mt]);

  const translateFieldConfig = useCallback(
    (fieldConfig: FieldConfig): FieldConfig => {
      if (!i18nKey) return fieldConfig;
      return {
        ...fieldConfig,
        label: fieldConfig.label
          ? translateFieldLabel(mt, fieldConfig.name, fieldConfig.label)
          : fieldConfig.label,
        placeholder: translateFieldPlaceholder(mt, fieldConfig.name, fieldConfig.placeholder),
      };
    },
    [i18nKey, mt],
  );

  // Build field tree on every render. When fieldOptions is provided, use it so selects get live options (e.g. from useClientsForSelect).
  // disabledOverride per field so only fields in toggleable sections re-render when section edit state changes (FormFieldWithLocalState is memoized).
  const renderedFields: Record<string, React.ReactNode> = {};
  config.fields.forEach((fieldConfig) => {
    const liveOptions = fieldOptions?.[fieldConfig.name];
    const fieldConfigWithOptions =
      liveOptions && Array.isArray(liveOptions)
        ? { ...fieldConfig, options: liveOptions }
        : fieldConfig;
    const localizedField = translateFieldConfig(fieldConfigWithOptions);
    const sectionId = fieldToSectionMap[localizedField.name];
    const formDisabledBySection = sectionId != null && sectionEditState[sectionId] === false;
    const disabledOverride = formDisabledBySection || isFormReadOnlyByConfig;
    renderedFields[localizedField.name] = (
      <FormFieldWithLocalState
        key={localizedField.name}
        fieldConfig={localizedField}
        error={formErrors[localizedField.name]}
        onBlur={handleBlur}
        config={config}
        disabledOverride={disabledOverride}
        isEdit={isEdit}
      />
    );
  });

  const formConfig = isEdit ? config.form.edit : config.form.new;
  const contentMaxWidthClass = config.form.contentMaxWidthClass ?? 'max-w-6xl';

  if (isEdit && loadingData && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner
          size="large"
          message={
            i18nKey
              ? mt('form.loadingText', resolveCommonAction(t, config.form.loadingText ?? t('states.loading')))
              : resolveCommonAction(t, config.form.loadingText ?? t('states.loading'))
          }
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className={`${isMobile ? 'w-full px-2' : `${contentMaxWidthClass} mx-auto w-full`} flex flex-col h-full`}>
        {/* Caption is shown in the topbar (MainLayout sets title from config.form.new/edit.title) — no in-page header */}

        {/* Error Message - Fixed */}
        {error && (
          <div className={`flex-shrink-0 ${isMobile ? 'mb-3' : 'mb-4'} bg-red-50 border border-red-200 rounded-lg ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'}`}>
            <p className={`text-sm text-red-800 ${isMobile ? 'text-xs' : ''}`}>{error}</p>
          </div>
        )}

        {/* Read-only notice when form is disabled by config (e.g. offer already accepted/declined) */}
        {isEdit && data && isFormReadOnlyByConfig && config.form.disabledMessage && (
          <div className={`flex-shrink-0 ${isMobile ? 'mb-3' : 'mb-4'} bg-amber-50 border border-amber-200 rounded-lg ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'}`}>
            <p className={`text-sm text-amber-800 ${isMobile ? 'text-xs' : ''}`}>
              {typeof config.form.disabledMessage === 'function' ? config.form.disabledMessage(data) : config.form.disabledMessage}
            </p>
          </div>
        )}

        {/* Form - Scrollable */}
        <form
          key={formKey}
          onSubmit={handleSubmit}
          className={`flex-1 flex flex-col ${colors.surface} rounded-lg shadow-sm border ${colors.border} overflow-hidden ${(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? 'rounded-md' : ''}`}
        >
          <FormDataContext.Provider value={formDataContextValue}>
          {(config.form as { bootstrap?: React.ReactNode }).bootstrap ?? null}
          {/* Scrollable Form Content */}
          <div className={`flex-1 overflow-y-auto dashboard-scrollbar ${(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? 'px-3 pt-3 pb-3' : isMobile ? 'px-3 pt-3 pb-3' : 'px-5 pt-4 pb-4'}`}>
            {(config as { renderEditFormHeader?: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode }).renderEditFormHeader && (
              <div className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? 'mb-3' : 'mb-6'}>
                {(config as { renderEditFormHeader: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode }).renderEditFormHeader({
                  isEdit: !!isEdit,
                  data: data,
                  id: Number.isInteger(editId) ? editId : undefined,
                })}
              </div>
            )}
            <FormLayoutRenderer
              layout={localizedFormLayout}
              fields={renderedFields}
              formData={formDataRef.current}
              isMobile={isMobile}
              variant={formLayoutVariant}
              sectionEditState={sectionEditState}
              onSectionEditToggle={onSectionEditToggle}
            />

            {/* Form extra content (e.g. catalog item product photos: message on new, upload+gallery on edit) */}
            {(config as { renderEditFormExtra?: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode }).renderEditFormExtra && (
              <div className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? `mt-3 pt-3 border-t ${colors.border}` : `mt-6 pt-6 border-t ${colors.border}`}>
                {(config as { renderEditFormExtra: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode }).renderEditFormExtra({
                  isEdit: !!isEdit,
                  data: data,
                  id: Number.isInteger(editId) ? editId : undefined,
                })}
              </div>
            )}

            {/* Custom Fields Section */}
            {(() => {
              const entityType = getEntityTypeFromRoute(config.module.route.list) || 
                                 getEntityTypeFromTitle((config.module as any).title || '');
              const entityId = isEdit && Number.isInteger(editId) ? editId : null;
              const customFieldValuesForNew = !entityId ? (() => {
                const out: Record<number, any> = {};
                Object.entries(formDataRef.current).forEach(([key, value]) => {
                  if (key.startsWith('_customFields_')) {
                    const fieldId = parseInt(key.replace('_customFields_', ''), 10);
                    if (!isNaN(fieldId) && value !== undefined) out[fieldId] = value;
                  }
                });
                return out;
              })() : undefined;
              if (entityType) {
                return (
                  <div className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? `mt-3 pt-3 border-t ${colors.border}` : `mt-4 pt-4 border-t ${colors.border}`}>
                    <h3 className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? `text-sm font-semibold ${colors.text} mb-2` : `text-base font-semibold ${colors.text} mb-3`}>Custom Fields</h3>
                    <DynamicFieldRenderer
                      entityType={entityType}
                      entityId={entityId}
                      valuesForNewEntity={customFieldValuesForNew}
                      onValueChange={(fieldId, value) => {
                        if (!entityId) setFieldValue(`_customFields_${fieldId}`, value);
                      }}
                    />
                  </div>
                );
              }
              return null;
            })()}

            {/* File attachments section (when form supports files) */}
            {supportsFiles && (
              <div className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? `mt-3 pt-3 border-t ${colors.border}` : `mt-6 pt-6 border-t ${colors.border}`}>
                <FileAttachmentsFormBlock
                  label="Attachments"
                  hint="Upload documents, images, or other files. You can add multiple files. Click a file name or the view icon to open it in a new tab."
                  newFiles={files}
                  existingFiles={visibleExistingFiles.map((f: { id: number; fileName?: string }) => ({ id: f.id, fileName: f.fileName }))}
                  onNewFilesChange={handleFileChange}
                  onRemoveExisting={handleRemoveExistingFile}
                  getViewFileUrl={getViewFileUrlForEdit}
                />
              </div>
            )}
          </div>

          {/* Form Actions - Fixed at bottom, sticky on mobile */}
          <div
            className={`flex-shrink-0 flex ${isMobile ? 'flex-col-reverse gap-2' : 'items-center justify-between gap-3'} ${(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? 'p-3' : isMobile ? 'p-4' : 'p-6'} border-t ${colors.border} ${colors.surfaceHover} ${isMobile ? 'sticky bottom-0 z-10 shadow-lg' : ''}`}
          >
            {canDelete ? (
              <Button
                type="button"
                variant="danger"
                onClick={() => setDeleteOpen(true)}
                disabled={isSubmitting || deleteLoading}
                className={isMobile ? 'w-full min-h-[44px]' : ''}
              >
                {t('actions.delete')}
              </Button>
            ) : (
              <span className={isMobile ? 'hidden' : ''} />
            )}
            <div className={`flex ${isMobile ? 'flex-col-reverse gap-2 w-full' : 'items-center gap-3'}`}>
            <Button 
              type="button" 
              onClick={handleCancel} 
              variant="outline"
              className={isMobile ? 'w-full min-h-[44px]' : ''}
            >
              {resolveCommonAction(t, config.form.cancelText)}
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || isFormReadOnlyByConfig || deleteLoading} 
              loading={isSubmitting}
              className={isMobile ? 'w-full min-h-[44px]' : ''}
            >
              {i18nKey
                ? mt(isEdit ? 'form.edit.submitText' : 'form.new.submitText', formConfig.submitText)
                : resolveCommonAction(t, formConfig.submitText)}
            </Button>
            </div>
          </div>
          </FormDataContext.Provider>
        </form>
        {deleteConfig ? (
          <ConfirmationDialog
            isOpen={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            onConfirm={() => void handleDeleteRecord()}
            title={i18nKey ? mt('delete.title', deleteConfig.title) : deleteConfig.title}
            message={i18nKey ? mt('delete.message', deleteConfig.message) : deleteConfig.message}
            confirmText={
              i18nKey
                ? resolveCommonAction(t, mt('delete.confirmText', deleteConfig.confirmText))
                : resolveCommonAction(t, deleteConfig.confirmText)
            }
            cancelText={
              i18nKey
                ? resolveCommonAction(t, mt('delete.cancelText', deleteConfig.cancelText))
                : resolveCommonAction(t, deleteConfig.cancelText)
            }
            confirmVariant="danger"
            isLoading={deleteLoading}
          />
        ) : null}
      </div>
    </div>
  );
}

export default GenericFormPage;

